import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import getDayRangeString from "../../../utils/day-range-string";
import fromIntensity from "../../../utils/get-zone";
import MetricsBackButton from "./metrics-back-button";
import set from "@rill/set";
const REP_RANGE = _.range(1, 13);
const RPE_RANGE = _.reverse(_.range(5, 10.5, 0.5));
const CALC_REP_RANGE = _.range(-12, 23);
const CALC_RPE_RANGE = _.reverse(_.range(-7, 22.5, 0.5));
const MAX_CALC_RPE = _.max(CALC_RPE_RANGE);
const MIN_CALC_RPE = _.min(CALC_RPE_RANGE);
const MAX_CALC_REP = _.max(CALC_REP_RANGE);
const MIN_CALC_REP = _.min(CALC_REP_RANGE);

import { RPE_STANDARD_TABLE, DATE_FORMATS } from "../../../constants";
import convertToRiR from "../../../utils/converters/convert-intensity";
import NavigationComponent from "../calendar/components/NavigationComponent";
import SvgIcon from "../../../parts/SvgIcon";
import { ReportsBackHandler } from "../calendar/components/ReportsBackHandler";
import { AthleteLinks } from "../../../parts/athlete-links";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { useTranslation } from "react-i18next";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const { DATE_PICKER_FORMAT, DATE_DAY_FORMAT } = DATE_FORMATS;

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("athlete-portal");

  const { query, search, activeUser, activeUserToRequest, athlete, pathname } =
    locals;
  const activeMetrics = _.cloneDeep(
    _.get(locals, ["exercisesRPETable", query.type]) || {}
  );
  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  const activeTable =
    query.formula === "standard"
      ? RPE_STANDARD_TABLE
      : calculateEstimates(activeMetrics);
  const activeRepMax = _.get(
    athlete || activeUser,
    `repMaximums.${query.type}`,
    0
  );
  const intensityMeasure =
    _.get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";
  return (
    <main role="reports-wrapper">
      <NavigationComponent
        query={query}
        pathName={"/athlete-portal/metrics"}
        textKey="nav.calendarDayView"
        classNames="mobile-hidden"
      />
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/athlete-portal/metrics/${search}`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/athlete-portal/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/rpe-custom-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/rpe-custom-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3>{t("metrics.customTables", { measure: intensityMeasure })}</h3>
              <ToggleLinksInput
                name="display"
                options={[
                  { text: t("rpeTable.oneRmPercent"), value: "repMax" },
                  { text: t("metricsDrawer.kg"), value: "metric" },
                  { text: t("metricsDrawer.lbs"), value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                {t("rpeTable.description", { measure: intensityMeasure })}
              </p>
            </div>
            <p className="p4 only-show-mobile">
              {t("rpeTable.description", { measure: intensityMeasure })}
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <form id="metrics-form" action="/actions/refresh">
              <div role="calendar-date-selectors">
                <DateInput
                  name="start"
                  placeholder="Start Date"
                  value={query.start}
                  max={query.end}
                  onChange={submitForm}
                />
                <span>to</span>
                <DateInput
                  name="end"
                  placeholder="End Date"
                  value={query.end}
                  min={query.start}
                  onChange={submitForm}
                  alignRight={true}
                />
              </div>
              <div>
                <select
                  name="formula"
                  className="select-input"
                  onChange={submitForm}
                  defaultValue={query.formula}
                >
                  <option value="custom">
                    {t("rpeTable.yourPersonalTable", { measure: intensityMeasure })}
                  </option>
                  <option value="standard">{t("rpeTable.industryStandard")}</option>
                </select>
              </div>
            </form>
          </hgroup>

          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="display"
              options={[
                { text: t("rpeTable.oneRmPercent"), value: "repMax" },
                { text: t("metricsDrawer.kg"), value: "metric" },
                { text: t("metricsDrawer.lbs"), value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />

            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("strengthMetricsShared.squat"), value: "squat" },
                { text: t("strengthMetricsShared.bench"), value: "bench" },
                { text: t("strengthMetricsShared.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>
        <div className="legend">
          <div className="bar series max">{t("rpeTable.max")}</div>
          <div className="bar series heavy">{t("rpeTable.heavy")}</div>
          <div className="bar series medium">{t("rpeTable.medium")}</div>
          <div className="bar series light">{t("rpeTable.light")}</div>
          <div className="bar series deload">{t("rpeTable.deload")}</div>
        </div>
        <section role="table-wrapper" className="rpe-table">
          <table role="report-table">
            <thead>
              <tr>
                <th>{intensityMeasure}</th>
                <th>{t("rpeTable.oneRep")}</th>
                <th>2</th>
                <th>3</th>
                <th>4</th>
                <th>5</th>
                <th>6</th>
                <th>7</th>
                <th>8</th>
                <th>9</th>
                <th>10</th>
                <th>11</th>
                <th>12</th>
              </tr>
            </thead>
            <tbody>
              {_.map(RPE_RANGE, (rpe) => {
                if (!activeTable[rpe]) return;
                return (
                  <tr key={rpe}>
                    <td className="bold">{convertToRiR(rpe, !isRiR)}</td>
                    {_.map(REP_RANGE, (rep) => {
                      const result = _.get(activeTable, [rpe, rep]) || {};
                      const value = formatIntensity(
                        result.intensity,
                        result.repMax
                      );
                      return (
                        <td key={rep} className={result.zone}>
                          {result.date ? (
                            <a
                              href={
                                result.date &&
                                `/${
                                  query.athlete
                                    ? `athlete-portal/calendar/?athlete=${query.athlete}&`
                                    : "dashboard/calendar/?"
                                }view=day&date=${moment
                                  .utc(result.date)
                                  .format(DATE_DAY_FORMAT)}`
                              }
                            >
                              {value}
                            </a>
                          ) : (
                            value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </article>
    </main>
  );

  function formatIntensity(intensity, repMax) {
    if (!intensity || intensity <= 0) return "-";
    intensity = Math.min(intensity, 100);
    if (query.display === "repMax") return `${intensity}%`;
    const defaultRepMax = repMax || activeRepMax;
    return locals.withWeight(
      locals.fromMetric((intensity * defaultRepMax) / 100, query.display),
      query.display
    );
  }
}

/**
 * Estimates RPE table based one three strategies.
 */
function calculateEstimates(table) {
  let found = 0;

  // Blow up using up / right and down / left strategory.
  found += blowOut(table);

  // Estimate values by the average for the row.
  const foundInRow = (found += estimateRow(table));
  // If we find anything in row estimates then we blowout again.
  if (foundInRow) found += blowOut(table);

  // Estimate values by the average for the column.
  found += estimateColumn(table);

  // If we found any points this run then we try again.
  if (found) return calculateEstimates(table);

  // Otherwise we can't calculate anymore.
  return table;
}

/**
 * Calculates rpe estimates by blowing out the table.
 * Shifts existing data points diagonally (up and right, down and left).
 */
function blowOut(table) {
  let found = 0;
  _.forOwn(table, (reps, rpe) => {
    _.forOwn(reps, (data, rep) => {
      if (!data || !data.intensity) return;

      let posX = null;
      let posY = null;

      // up and right
      posX = rep;
      posY = rpe;
      while (
        ++posX < MAX_CALC_REP &&
        ++posY < MAX_CALC_RPE &&
        !_.get(table, [posY, posX])
      ) {
        _.set(table, [posY, posX], {
          intensity: data.intensity,
          zone: fromIntensity(data.intensity, posX),
        });
        found++;
      }

      // down and left
      posX = rep;
      posY = rpe;
      while (
        --posX > MIN_CALC_REP &&
        --posY > MIN_CALC_RPE &&
        !_.get(table, [posY, posX])
      ) {
        _.set(table, [posY, posX], {
          intensity: data.intensity,
          zone: fromIntensity(data.intensity, posX),
        });
        found++;
      }
    });
  });

  return found;
}

/**
 * Estimates data to right of a row (don't guess to the left).
 */
function estimateRow(table) {
  let found = 0;
  _.forOwn(table, (reps, rpe) => {
    rpe = Number(rpe);
    let previousIntensity = null;
    let previousRep = null;
    let activeDelta = null;
    let estimateReps = [];
    _.forEach(CALC_REP_RANGE, (rep, i) => {
      const data = _.get(reps, rep);

      if (data) {
        if (previousIntensity) {
          activeDelta =
            Math.abs(previousIntensity - data.intensity) /
            Math.abs(previousRep - rep);
        }

        previousRep = rep;
        previousIntensity = data.intensity;
      } else if (previousIntensity) {
        estimateReps.push(rep);
      }

      if (activeDelta && (data || i === CALC_REP_RANGE.length - 1)) {
        _.forEach(estimateReps, (rep) => {
          found++;
          const intensity = _.round(
            previousIntensity - (rep - previousRep) * activeDelta,
            1
          );
          _.set(table, [rpe, rep], {
            zone: fromIntensity(intensity),
            intensity,
          });
        });

        estimateReps = [];
      }
    });
  });

  return found;
}

/**
 * Estimates data down in a column (don't guess to the above).
 */
function estimateColumn(table) {
  let found = 0;
  _.forEach(CALC_REP_RANGE, (rep) => {
    let previousIntensity = null;
    let previousRpe = null;
    let activeDelta = null;
    let estimateRpes = [];

    _.forEach(RPE_RANGE, (rpe, i) => {
      const data = _.get(table, [rpe, rep]);

      if (data) {
        if (previousIntensity) {
          activeDelta =
            Math.abs(previousIntensity - data.intensity) /
            Math.abs(previousRpe - rpe);
        }

        previousRpe = rpe;
        previousIntensity = data.intensity;
      } else if (previousIntensity) {
        estimateRpes.push(rpe);
      }

      if (activeDelta && (data || i === RPE_RANGE.length - 1)) {
        _.forEach(estimateRpes, (rpe) => {
          found++;
          const intensity = _.round(
            previousIntensity - (previousRpe - rpe) * activeDelta,
            1
          );
          _.set(table, [rpe, rep], {
            zone: fromIntensity(intensity),
            intensity,
          });
        });

        estimateRpes = [];
      }
    });
  });

  return found;
}

function submitForm() {
  submit("/actions/refresh", { body: document.getElementById("metrics-form") });
}

const RpeTable = (app) => {
  app.get(
    "/rpe-table",
    set("heading", "RPE Custom Tables"),
    async ({ req, res, load, locals, api, fail }) => {
      const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
        locals.organizationRoleName
      );

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        await load("allAthletesFromOrganization");
      }

      const { query } = req;
      _.defaults(query, {
        type: "squat",
        formula: "standard",
        display: "repMax",
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await load("exercisesRPETable", {
        start: moment
          .utc(query.start, DATE_PICKER_FORMAT)
          .startOf("day")
          .toDate(),
        end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
        proxyAthlete: query.athlete,
        reqSource: "metrics",
      });

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  user: {
                    name: locals.athlete.name,
                    registered: locals.athlete.registered,
                    avatar: locals.athlete.avatar,
                    units: locals.athlete.units,
                  },
                  type: "rpe-table",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesRPETable"])
                      .assign({
                        start: query.start,
                        end: query.end,
                        formula: query.formula,
                        repMax: _.get(
                          locals.activeUser,
                          `repMaximums.${query.type}`,
                          0
                        ),
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);

          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }

      locals.modal = {
        share: <ShareModal />,
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default RpeTable;
